import React, { FunctionComponent, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  TimelineEntry as ITimelineEntry,
  PublishingSource,
} from '../../../../types/__generated__';
import ConfirmationModal from '../../ConfirmationModal/ConfirmationModal';
import Modal from '../../Modal/Modal';
import { StravaAvatarBadge } from '../StravaTimelineEntry/StravaAvatarBadge';
import { StravaLink } from '../StravaTimelineEntry/StravaLink';
import { Body } from './Body';
import { Footer } from './Footer';
import { Header } from './Header';

import styles from '../Timeline.scss';

export interface TimelineEntryProps {
  timelineEntry: ITimelineEntry;
  avatarUrl?: string | null;
  postedBy?: ReactNode;
  onDeleteEntry?: (id: string) => void;
}

const TimelineEntry: FunctionComponent<TimelineEntryProps> = ({
  timelineEntry,
  avatarUrl,
  postedBy,
  onDeleteEntry,
}) => {
  const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState(false);
  const { t } = useTranslation('common', { useSuspense: false });
  const { id, fitnessActivity } = timelineEntry;
  const activityId = fitnessActivity?.activityId ?? null;
  const isStravaActivity =
    timelineEntry.publishingSource === PublishingSource.Strava;

  const shouldDisableRefreshButton = () => {
    if (!timelineEntry.refreshRequestedTime) {
      return false;
    }
    const earliestTimeToEnableRefreshButton = new Date(
      timelineEntry.refreshRequestedTime,
    );
    earliestTimeToEnableRefreshButton.setSeconds(
      earliestTimeToEnableRefreshButton.getSeconds() + 1,
    );

    return new Date(Date.now()) < earliestTimeToEnableRefreshButton;
  };

  const handleDeleteEntry = () => {
    if (onDeleteEntry) {
      setConfirmationModalIsOpen(true);
    }
  };

  const postedByWrapper = isStravaActivity ? (
    <>
      <strong>{postedBy}</strong> via <strong>Strava</strong>
    </>
  ) : (
    postedBy
  );
  const avatarBadge = isStravaActivity ? <StravaAvatarBadge /> : undefined;

  return (
    <div className={styles.timeline} data-qa="timeline-entry">
      <>
        <Header
          timelineEntry={timelineEntry}
          avatarUrl={avatarUrl}
          postedBy={postedByWrapper}
          avatarBadge={avatarBadge}
          onDeleteEntry={handleDeleteEntry}
          disableRefreshButton={shouldDisableRefreshButton()}
        />
        <Body timelineEntry={timelineEntry} />
        <Footer timelineEntry={timelineEntry}>
          {activityId && <StravaLink activityId={activityId} />}
        </Footer>
        {confirmationModalIsOpen && (
          <Modal
            onCloseRequest={() => setConfirmationModalIsOpen(false)}
            data-qa={'delete-confirmation-modal'}
          >
            <ConfirmationModal
              modalTitle={
                timelineEntry.fitnessActivity
                  ? t('timeline.deleteTimelineEntry.modal.fitnessTitle')
                  : t('timeline.deleteTimelineEntry.modal.nonFitnessTitle')
              }
              confirmButtonText={t(
                'timeline.deleteTimelineEntry.modal.confirm',
              )}
              dismissButtonText={t(
                'timeline.deleteTimelineEntry.modal.dismiss',
              )}
              onDismiss={() => setConfirmationModalIsOpen(false)}
              onConfirm={() => (onDeleteEntry ? onDeleteEntry(id) : {})}
            >
              <p>
                {timelineEntry.fitnessActivity
                  ? t(
                      'timeline.deleteTimelineEntry.modal.fitnessConfirmationMessage',
                    )
                  : t(
                      'timeline.deleteTimelineEntry.modal.nonFitnessConfirmationMessage',
                    )}
              </p>
            </ConfirmationModal>
          </Modal>
        )}
      </>
    </div>
  );
};
TimelineEntry.displayName = 'TimelineEntry';

export default TimelineEntry;
